import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Loader2, RefreshCw, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const RECOMMENDER_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/recommend-package`;

const EXAMPLE_PROMPTS = [
  "I'm getting married next summer and want beautiful cinematic coverage",
  "I run a restaurant and need content for Instagram and TikTok",
  "We're celebrating our 10th anniversary and want to capture this milestone",
  "I'm a model looking to build my video portfolio",
  "We're hosting a corporate conference for 200 people",
];

export const PackageRecommender = () => {
  const [needs, setNeeds] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasResult, setHasResult] = useState(false);

  const getRecommendation = async () => {
    if (!needs.trim() || isLoading) return;

    setIsLoading(true);
    setRecommendation("");
    setHasResult(false);

    try {
      const response = await fetch(RECOMMENDER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ needs: needs.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to get recommendation");
      }

      if (!response.body) throw new Error("No response body");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullRecommendation = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              fullRecommendation += content;
              setRecommendation(fullRecommendation);
            }
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }

      setHasResult(true);
    } catch (error) {
      console.error("Recommendation error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to get recommendation");
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setNeeds("");
    setRecommendation("");
    setHasResult(false);
  };

  const useExample = (example: string) => {
    setNeeds(example);
  };

  return (
    <section className="py-24 bg-card">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">AI-Powered</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
            Find Your Perfect Package
          </h2>
          <p className="text-muted-foreground">
            Tell us about your project and our AI will recommend the best 
            videography services for your needs.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-2xl mx-auto"
        >
          <AnimatePresence mode="wait">
            {!hasResult ? (
              <motion.div
                key="input"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <div className="bg-background rounded-xl p-6 border border-border">
                  <label className="block text-sm font-medium mb-3">
                    Describe what you're looking for:
                  </label>
                  <Textarea
                    value={needs}
                    onChange={(e) => setNeeds(e.target.value)}
                    placeholder="E.g., I'm planning a destination wedding in Italy and want cinematic coverage of the whole weekend..."
                    className="min-h-[120px] resize-none"
                    disabled={isLoading}
                  />
                  
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs text-muted-foreground">Try:</span>
                    {EXAMPLE_PROMPTS.slice(0, 3).map((example, index) => (
                      <button
                        key={index}
                        onClick={() => useExample(example)}
                        disabled={isLoading}
                        className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                      >
                        {example.length > 40 ? example.substring(0, 40) + "..." : example}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={getRecommendation}
                  disabled={!needs.trim() || isLoading}
                  size="lg"
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Finding your perfect package...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Get Recommendations
                    </>
                  )}
                </Button>

                {isLoading && recommendation && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-background rounded-xl p-6 border border-border"
                  >
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <p className="whitespace-pre-wrap">{recommendation}</p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <div className="bg-background rounded-xl p-6 border border-border">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <span className="font-medium">Our Recommendations</span>
                  </div>
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <p className="whitespace-pre-wrap text-muted-foreground leading-relaxed">
                      {recommendation}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={reset} variant="outline" className="flex-1">
                    <RefreshCw className="w-4 h-4" />
                    Start Over
                  </Button>
                  <Button asChild className="flex-1">
                    <Link to="/contact">
                      Get a Custom Quote
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
